import { useAuth } from "@elit-hair/elit-tools";
import { useMutation, useQuery } from "@tanstack/react-query";

import { callSvcAxiosInstance } from "@/constants";
import { SettingsDefinition } from "@/providers/general/settings-provider";
import { queryClient } from "@/utils/query-client";

type GetSettingResponse = {
    userId: string;
    configData: SettingsDefinition;
    createdAt: string;
    updatedAt: string;
    id: string;
};

const queryKeys = {
    currentSettings: () => ["current_settings"] as const,
    userConfig: (userId: string) => ["user_config", userId] as const,
};
/** Payload for PATCH: partial config keys and values (arrays/objects allowed, e.g. QUALITY_WATCH_PRESETS, DASHBOARD_COLUMN_LAYOUT). */
type RemoteConfigPatchPayload = Partial<SettingsDefinition>;

const endpoints = {
    get: async () => await callSvcAxiosInstance.get<GetSettingResponse>(`/remote-config`),

    update: async (data: RemoteConfigPatchPayload) => await callSvcAxiosInstance.patch(`/remote-config`, data),
    reset: async () => await callSvcAxiosInstance.put(`/remote-config/reset`),
    getUserConfig: async (userId: string) => await callSvcAxiosInstance.get(`/remote-config/admin/user/${userId}`),
    updateUserConfig: async (userId: string, data: RemoteConfigPatchPayload) =>
        await callSvcAxiosInstance.patch(`/remote-config/admin/user/${userId}`, data),
    resetConfigForUser: async (userId: string) =>
        await callSvcAxiosInstance.put(`/remote-config/admin/user/${userId}/reset`),
    // TODO: Add bulk update for admins
    // bulkUpdate: async (data: Record<string, string>) =>
    //     await callSvcAxiosInstance.patch(`/remote-config/admin/bulk`, data),
};

// --- Current Settings ---
// Get current settings
export const useCurrentSettings = () => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: queryKeys.currentSettings(),
        queryFn: async () => {
            const response = await endpoints.get();
            return response.data?.configData;
        },
        enabled: !!auth, // Only run when authenticated
    });
};

// Update current settings. We do not invalidate/refetch on success so that the provider's
// optimistic state (from setValue) is not overwritten by a possibly stale GET response.
export const useUpdateCurrentConfig = () => {
    return useMutation({
        mutationFn: (data: RemoteConfigPatchPayload) => endpoints.update(data),
    });
};

// Reset current config
export const useResetCurrentConfig = () => {
    return useMutation({
        mutationFn: () => endpoints.reset(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.currentSettings() });
        },
    });
};

// --- Admins ---
// Update user config
export const useGetUserConfig = (userId?: string) => {
    return useQuery({
        queryKey: queryKeys.userConfig(userId || ""),
        queryFn: async () => {
            const response = await endpoints.getUserConfig(userId || "");
            return response.data?.configData;
        },
        enabled: !!userId,
        staleTime: 0,
    });
};

export const useUpdateUserConfig = (userId: string) => {
    return useMutation({
        mutationFn: (data: RemoteConfigPatchPayload) => endpoints.updateUserConfig(userId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.userConfig(userId) });
        },
    });
};

// Reset config for user
export const useResetConfigForUser = (userId: string) => {
    return useMutation({
        mutationFn: () => endpoints.resetConfigForUser(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.currentSettings() });
        },
    });
};
